import React from 'react';
import PropTypes from 'prop-types';
import { formatNumber } from '../../../utils/formatting';

/**
 * Two series as grouped monthly bars, each on its own scale (today's
 * dual-axis trend chart: e.g. impressions left, views right). Colours are
 * token tone classes (u2-tone-*), so the chart follows the theme. Accessible:
 * an image with a label, and a visually hidden table with every value.
 */
export default function PairedBarChart({ label, labels, a, b, height = 170 }) {
    const W = 640;
    const H = height;
    const P = { l: 44, r: 44, t: 10, b: 24 };
    const maxA = Math.max(1, ...a.values);
    const maxB = Math.max(1, ...b.values);
    const n = labels.length;
    const slot = (W - P.l - P.r) / Math.max(1, n);
    const bw = Math.min(22, slot * 0.32);
    const plotH = H - P.t - P.b;
    const yOf = (v, max) => H - P.b - (v / max) * plotH;
    return (
        <div className="u2ov-bars">
            <div className="u2-legend" aria-hidden="true">
                <span className={`u2-tone-${a.tone}`}><i />{a.name}</span>
                <span className={`u2-tone-${b.tone}`}><i />{b.name}</span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={label} focusable="false">
                <line className="u2-ch-base" x1={P.l} x2={W - P.r} y1={H - P.b} y2={H - P.b} />
                <text className="u2-ch-ax" x={P.l - 6} y={P.t + 8} textAnchor="end">{formatNumber(maxA)}</text>
                <text className="u2-ch-ax" x={W - P.r + 6} y={P.t + 8} textAnchor="start">{formatNumber(maxB)}</text>
                {labels.map((lab, i) => {
                    const cx = P.l + slot * i + slot / 2;
                    return (
                        <g key={lab}>
                            <rect className={`u2ov-bar u2-tone-${a.tone}`} x={cx - bw - 1} y={yOf(a.values[i], maxA)} width={bw} height={Math.max(0, H - P.b - yOf(a.values[i], maxA))} rx="3">
                                <title>{`${lab} · ${a.name}: ${formatNumber(a.values[i])}`}</title>
                            </rect>
                            <rect className={`u2ov-bar u2-tone-${b.tone}`} x={cx + 1} y={yOf(b.values[i], maxB)} width={bw} height={Math.max(0, H - P.b - yOf(b.values[i], maxB))} rx="3">
                                <title>{`${lab} · ${b.name}: ${formatNumber(b.values[i])}`}</title>
                            </rect>
                            <text className="u2-ch-ax" x={cx} y={H - 6} textAnchor="middle">{lab}</text>
                        </g>
                    );
                })}
            </svg>
            <div className="v2-sr u2-sr-box">
            <table>
                <caption>{label}</caption>
                <thead><tr><th scope="col">Month</th><th scope="col">{a.name}</th><th scope="col">{b.name}</th></tr></thead>
                <tbody>
                    {labels.map((lab, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <tr key={`${i}-${lab}`}><th scope="row">{lab}</th><td>{formatNumber(a.values[i])}</td><td>{formatNumber(b.values[i])}</td></tr>
                    ))}
                </tbody>
            </table>
            </div>
        </div>
    );
}

const seriesShape = PropTypes.shape({ name: PropTypes.string.isRequired, values: PropTypes.arrayOf(PropTypes.number).isRequired, tone: PropTypes.string.isRequired });
PairedBarChart.propTypes = {
    label: PropTypes.string.isRequired,
    labels: PropTypes.arrayOf(PropTypes.string).isRequired,
    a: seriesShape.isRequired,
    b: seriesShape.isRequired,
    height: PropTypes.number,
};
